//src/pages/TikpagExperience/TikpagExperienceApuracoes.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Award,
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  Medal,
  RefreshCcw,
  Search,
  Users
} from "lucide-react";
import axios from "../../axiosConfig";
import "./TikpagExperienceApuracoes.css";

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

const hoje = new Date();

const dinheiro = (valor) =>
  Number(valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });

const dataBr = (valor) => {
  if (!valor) return "-";
  const [ano, mes, dia] = String(valor).slice(0, 10).split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : valor;
};

export default function TikpagExperienceApuracoes() {
  const [campanhas, setCampanhas] = useState([]);
  const [campanhaId, setCampanhaId] = useState("");
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [previa, setPrevia] = useState(null);
  const [busca, setBusca] = useState("");
  const [resultadoFiltro, setResultadoFiltro] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingCampanhas, setLoadingCampanhas] = useState(true);
  const [erro, setErro] = useState("");

  const [processando, setProcessando] =
    useState(false);

  const [resultadoProcessamento, setResultadoProcessamento] =
    useState(null);

  const [sucesso, setSucesso] =
    useState("");

    const [reprocessando, setReprocessando] =
      useState(false);

    const [resultadoReprocessamento, setResultadoReprocessamento] =
      useState(null);

  const carregarCampanhas = useCallback(async () => {
      setLoadingCampanhas(true);
      setErro("");

    try {
      const { data } = await axios.get("/admin/experience/campanhas");
      const lista = Array.isArray(data?.campanhas) ? data.campanhas : [];
      setCampanhas(lista);
      setCampanhaId((atual) => {
        if (atual) return atual;
        const preferida =
          lista.find((item) => item.status === "EM_ANDAMENTO") ||
          lista.find((item) => item.status === "INSCRICOES") ||
          lista[0];
        return preferida ? String(preferida.id) : "";
      });
    } catch (err) {
      setErro(err.response?.data?.error || "Não foi possível carregar as campanhas.");
    } finally {
      setLoadingCampanhas(false);
    }
  }, []);

  useEffect(() => {
    carregarCampanhas();
  }, [carregarCampanhas]);

  const gerarPrevia = async (
      preservarResultado = false
    ) => {
    if (!campanhaId) {
      setErro("Selecione uma campanha.");
      return;
    }

    setLoading(true);
    setErro("");
    setPrevia(null);

    if (!preservarResultado) {
        setSucesso("");
        setResultadoProcessamento(null);
        setResultadoReprocessamento(null);
      }

    try {
      const { data } = await axios.get("/admin/experience/apuracoes/previa", {
        params: { campanha_id: campanhaId, ano, mes }
      });
      setPrevia(data);
    } catch (err) {
      setErro(err.response?.data?.error || "Não foi possível gerar a prévia da apuração.");
    } finally {
      setLoading(false);
    }
  };

  const processarApuracao = async (simular) => {
  if (!campanhaId || !ano || !mes) {
    setErro(
      "Selecione campanha, ano e mês."
    );
    return;
  }

  if (!simular && !previa?.pode_concluir) {
    setErro(
      "Esta competência ainda não pode ser concluída."
    );
    return;
  }

  if (!simular) {
    const confirmou = window.confirm(
      `Confirma o fechamento definitivo de ${competenciaTexto}?\n\n` +
        "Essa ação criará as apurações e conquistas dos participantes."
    );

    if (!confirmou) return;
  }

  setProcessando(true);
  setErro("");
  setSucesso("");
  setResultadoProcessamento(null);

  try {
    const resposta = await axios.post(
      "/admin/experience/apuracoes/concluir",
      {
        campanha_id:
          Number(campanhaId),

        ano:
          Number(ano),

        mes:
          Number(mes),

        simular
      }
    );

    setResultadoProcessamento(
      resposta.data || null
    );

    setSucesso(
      resposta.data?.message ||
        (
          simular
            ? "Simulação concluída com sucesso."
            : "Apuração concluída com sucesso."
        )
    );

 if (!simular) {
  await gerarPrevia(true);
}
  } catch (err) {
    console.error(
      "Erro ao processar apuração Experience:",
      err
    );

    setErro(
      err.response?.data?.error ||
        "Não foi possível processar a apuração."
    );
  } finally {
    setProcessando(false);
  }
};

const reprocessarCompetencia = async (
  simular
) => {
  const fechamentoId =
    previa?.fechamento_existente?.id;

  if (!fechamentoId) {
    setErro(
      "Não foi possível identificar o fechamento desta competência."
    );
    return;
  }

  if (!simular) {
    const novasConquistas =
      Number(
        resultadoReprocessamento
          ?.resumo?.novas_conquistas || 0
      );

    const alteracoes =
      Number(
        resultadoReprocessamento
          ?.resumo?.apuracoes_alteradas || 0
      );

    const revisoes =
      Number(
        resultadoReprocessamento
          ?.resumo?.revisoes_necessarias || 0
      );

    const confirmou =
      window.confirm(
        `Confirma o reprocessamento de ${competenciaTexto}?\n\n` +
        `${alteracoes} apuração(ões) serão atualizadas.\n` +
        `${novasConquistas} nova(s) conquista(s) serão liberadas.\n` +
        `${revisoes} caso(s) exigem revisão administrativa.\n\n` +
        "Conquistas já concedidas não serão removidas automaticamente."
      );

    if (!confirmou) return;
  }

  setReprocessando(true);
  setErro("");
  setSucesso("");

  if (simular) {
    setResultadoReprocessamento(null);
  }

  try {
    const resposta =
      await axios.post(
        "/admin/experience/apuracoes/reprocessar",
        {
          fechamento_id:
            Number(fechamentoId),

          simular
        }
      );

    setResultadoReprocessamento(
      resposta.data || null
    );

    setSucesso(
      resposta.data?.message ||
        (
          simular
            ? "Verificação concluída com sucesso."
            : "Competência reprocessada com sucesso."
        )
    );

    if (!simular) {
      /*
       * Recarrega a prévia para que cards,
       * tabela e totais reflitam o fechamento
       * atualizado.
       *
       * preservarResultado=true mantém na tela
       * o resultado do reprocessamento.
       */
      await gerarPrevia(true);
    }
  } catch (err) {
    console.error(
      "Erro ao reprocessar competência Experience:",
      err
    );

    setErro(
      err.response?.data?.error ||
        "Não foi possível reprocessar a competência."
    );
  } finally {
    setReprocessando(false);
  }
};

  const participantes = useMemo(() => {
    const lista = Array.isArray(previa?.participantes) ? previa.participantes : [];
    const termo = busca.trim().toLowerCase();

    return lista.filter((item) => {
      const combinaBusca =
        !termo ||
        [
          item.usuario_nome,
          item.usuario_email,
          item.empresa_nome,
          item.empresa_cnpj,
          item.pin_atingido?.nome
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(termo);

      const combinaResultado =
        !resultadoFiltro ||
        (resultadoFiltro === "PREMIADO" ? item.premiado : !item.premiado);

      return combinaBusca && combinaResultado;
    });
  }, [previa, busca, resultadoFiltro]);

  const maiorDistribuicao = Math.max(
    1,
    ...(previa?.distribuicao_pins || []).map((item) =>
      Number(item.total_premiados || 0)
    )
  );

  const fechamentoConcluido =
  previa?.fechamento_existente?.status ===
  "CONCLUIDO";

  const resumoReprocessamento =
  resultadoReprocessamento?.resumo || {};

const alteracoesReprocessamento =
  Array.isArray(
    resultadoReprocessamento?.alteracoes
  )
    ? resultadoReprocessamento.alteracoes
    : [];

const revisoesReprocessamento =
  Array.isArray(
    resultadoReprocessamento
      ?.revisoes_necessarias
  )
    ? resultadoReprocessamento
        .revisoes_necessarias
    : [];

const temAlteracoesReprocessamento =
  Number(
    resumoReprocessamento
      .apuracoes_alteradas || 0
  ) > 0;

const podeConfirmarReprocessamento =
  Boolean(
    resultadoReprocessamento?.simulacao
  ) &&
  temAlteracoesReprocessamento;

  const competenciaTexto =
  previa?.competencia
    ? `${MESES[
        Number(previa.competencia.mes) - 1
      ]} / ${previa.competencia.ano}`
    : `${MESES[Number(mes) - 1]} / ${ano}`;

  return (
    <div className="experienceApuracoes-page">
      <header className="experienceApuracoes-header">
        <div>
          <span className="experienceApuracoes-kicker">Tikpag Experience</span>
          <h1>Apurações mensais</h1>
          <p>Confira volumes, participantes e PINs antes do fechamento definitivo.</p>
        </div>

        <button
          type="button"
          className="experienceApuracoes-secondaryButton"
          onClick={carregarCampanhas}
          disabled={loadingCampanhas}
        >
          <RefreshCcw
            size={16}
            className={loadingCampanhas ? "experienceApuracoes-spin" : ""}
          />
          Atualizar campanhas
        </button>
      </header>

      {erro && (
        <div className="experienceApuracoes-alert erro">
          <AlertTriangle size={17} />
          {erro}
        </div>
      )}

      {sucesso && (
          <div className="experienceApuracoes-alert sucesso">
            <CheckCircle2 size={17} />
            {sucesso}
          </div>
        )}

      <section className="experienceApuracoes-filterPanel">
        <label className="experienceApuracoes-field">
          <span>Campanha</span>
          <select value={campanhaId} onChange={(e) => setCampanhaId(e.target.value)}>
            {!campanhas.length && <option value="">Nenhuma campanha</option>}
            {campanhas.map((campanha) => (
              <option key={campanha.id} value={campanha.id}>
                {campanha.nome}
              </option>
            ))}
          </select>
        </label>

        <label className="experienceApuracoes-field">
          <span>Ano</span>
          <input
            type="number"
            min="2020"
            max="2100"
            value={ano}
            onChange={(e) => setAno(Number(e.target.value))}
          />
        </label>

        <label className="experienceApuracoes-field">
          <span>Mês</span>
          <select value={mes} onChange={(e) => setMes(Number(e.target.value))}>
            {MESES.map((nome, indice) => (
              <option key={nome} value={indice + 1}>
                {nome}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="experienceApuracoes-primaryButton"
          onClick={gerarPrevia}
          disabled={loading || !campanhaId}
        >
          {loading ? (
            <RefreshCcw size={17} className="experienceApuracoes-spin" />
          ) : (
            <CalendarDays size={17} />
          )}
          Gerar prévia
        </button>
      </section>

      {previa && (
        <>
          <section className="experienceApuracoes-competencia">
            <div>
              <span>Competência analisada</span>
              <h2>{MESES[Number(previa.competencia?.mes) - 1]} / {previa.competencia?.ano}</h2>
              <p>
                Período considerado: {dataBr(previa.competencia?.data_inicio)} a {dataBr(previa.competencia?.data_fim)}
              </p>
            </div>

            <div className="experienceApuracoes-competenciaStatus">
              {previa.fechamento_existente?.status === "CONCLUIDO" ? (
                <span className="concluido">
                  <CheckCircle2 size={15} /> Fechamento concluído
                </span>
              ) : (
                <span className="previa">
                  <Search size={15} /> Prévia não gravada
                </span>
              )}
            </div>
          </section>

          <section className="experienceApuracoes-summary">
            {[
              [Users, "Participantes", previa.resumo?.total_participantes || 0, "Inscrições consideradas"],
              [Award, "Premiados", previa.resumo?.total_premiados || 0, "Atingiram pelo menos um PIN"],
              [CircleDollarSign, "Volume total", dinheiro(previa.resumo?.volume_total), "Operações aprovadas"],
              [Medal, "Taxa de premiação", `${Number(previa.resumo?.taxa_premiacao || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`, "Premiados sobre participantes"]
            ].map(([Icone, titulo, valor, legenda]) => (
              <article className="experienceApuracoes-summaryCard" key={titulo}>
                <div className="experienceApuracoes-summaryIcon">
                  <Icone size={20} />
                </div>
                <div>
                  <span>{titulo}</span>
                  <strong>{valor}</strong>
                  <small>{legenda}</small>
                </div>
              </article>
            ))}
          </section>

          <section className="experienceApuracoes-grid">
            <article className="experienceApuracoes-panel">
              <div className="experienceApuracoes-panelHeader">
                <div>
                  <h2>Distribuição por PIN</h2>
                  <p>Quantidade de participantes por maior nível atingido.</p>
                </div>
              </div>

              <div className="experienceApuracoes-distribution">
                {(previa.distribuicao_pins || []).map((item) => {
                  const total = Number(item.total_premiados || 0);
                  const largura = (total / maiorDistribuicao) * 100;

                  return (
                    <div className="experienceApuracoes-distributionItem" key={item.pin_id}>
                      <div className="experienceApuracoes-distributionLabel">
                        <div>
                          <span>{item.pin_nome}</span>
                          <small>{dinheiro(item.valor_minimo)}</small>
                        </div>
                        <strong>{total}</strong>
                      </div>
                      <div className="experienceApuracoes-bar">
                        <span style={{ width: `${largura}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </article>

            <article className="experienceApuracoes-panel">
              <div className="experienceApuracoes-panelHeader">
                <div>
                  <h2>Validação do fechamento</h2>
                  <p>Conferências necessárias antes da gravação.</p>
                </div>
              </div>

              <div className="experienceApuracoes-validation">
                {previa.impedimentos?.length ? (
                  previa.impedimentos.map((mensagem, indice) => (
                    <div className="experienceApuracoes-validationItem impedimento" key={`${mensagem}-${indice}`}>
                      <AlertTriangle size={17} />
                      <span>{mensagem}</span>
                    </div>
                  ))
                ) : (
                  <div className="experienceApuracoes-validationItem liberado">
                    <CheckCircle2 size={17} />
                    <span>A prévia não encontrou impedimentos para o fechamento.</span>
                  </div>
                )}

                <div className="experienceApuracoes-validationMeta">
                  <div>
                    <span>Operações</span>
                    <strong>{Number(previa.resumo?.total_operacoes || 0)}</strong>
                  </div>
                  <div>
                    <span>Sem premiação</span>
                    <strong>{Number(previa.resumo?.total_sem_meta || 0)}</strong>
                  </div>
                </div>
              </div>
            </article>
          </section>

          <section className="experienceApuracoes-panel experienceApuracoes-tablePanel">
            <div className="experienceApuracoes-tableToolbar">
              <div>
                <h2>Participantes da competência</h2>
                <p>Confira período individual, volume e PIN calculado.</p>
              </div>

              <div className="experienceApuracoes-tableFilters">
                <div className="experienceApuracoes-search">
                  <Search size={15} />
                  <input
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="Buscar participante ou empresa"
                  />
                </div>

                <select value={resultadoFiltro} onChange={(e) => setResultadoFiltro(e.target.value)}>
                  <option value="">Todos</option>
                  <option value="PREMIADO">Premiados</option>
                  <option value="SEM_META">Sem meta</option>
                </select>
              </div>
            </div>

            <div className="experienceApuracoes-tableWrapper">
              <table className="experienceApuracoes-table">
                <thead>
                  <tr>
                    <th>Participante</th>
                    <th>Empresa</th>
                    <th>Período</th>
                    <th>Operações</th>
                    <th>Volume</th>
                    <th>PIN</th>
                    <th>Próximo objetivo</th>
                  </tr>
                </thead>
                <tbody>
                  {participantes.map((participante) => (
                    <tr key={participante.usuario_id}>
                      <td>
                        <strong>{participante.usuario_nome}</strong>
                        <small>{participante.usuario_email}</small>
                      </td>
                      <td>
                        <strong>{participante.empresa_nome || "-"}</strong>
                        <small>{participante.empresa_cnpj || "-"}</small>
                      </td>
                      <td>
                        {dataBr(participante.periodo_considerado?.inicio)} a {dataBr(participante.periodo_considerado?.fim)}
                      </td>
                      <td>{Number(participante.quantidade_operacoes || 0)}</td>
                      <td><strong>{dinheiro(participante.volume_apurado)}</strong></td>
                      <td>
                        {participante.premiado ? (
                          <span className="experienceApuracoes-pinBadge premiado">
                            <Medal size={13} /> {participante.pin_atingido?.nome}
                          </span>
                        ) : (
                          <span className="experienceApuracoes-pinBadge semMeta">Sem PIN</span>
                        )}
                      </td>
                      <td>
                        {participante.proximo_pin ? (
                          <>
                            <strong>{participante.proximo_pin.nome}</strong>
                            <small>Faltam {dinheiro(participante.falta_para_proximo)}</small>
                          </>
                        ) : (
                          <span className="experienceApuracoes-maxLevel">Nível máximo</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {!participantes.length && (
                <div className="experienceApuracoes-empty">Nenhum participante encontrado.</div>
              )}
            </div>
          </section>

          {resultadoProcessamento && (
            <section className="experienceApuracoes-resultPanel">
              <div className="experienceApuracoes-panelHeader">
                <div>
                  <h2>
                    {resultadoProcessamento.simulacao
                      ? "Resultado da simulação"
                      : "Fechamento concluído"}
                  </h2>

                  <p>
                    {resultadoProcessamento.simulacao
                      ? "Nenhum registro foi gravado."
                      : "Os registros foram gravados definitivamente."}
                  </p>
                </div>

                <span
                  className={`experienceApuracoes-resultStatus ${
                    resultadoProcessamento.simulacao
                      ? "simulacao"
                      : "concluido"
                  }`}
                >
                  {resultadoProcessamento.simulacao
                    ? "Simulação"
                    : "Concluído"}
                </span>
              </div>

              <div className="experienceApuracoes-resultGrid">
                <div>
                  <span>Participantes</span>

                  <strong>
                    {Number(
                      resultadoProcessamento.resumo
                        ?.total_participantes ||
                        resultadoProcessamento.fechamento
                          ?.total_participantes ||
                        0
                    )}
                  </strong>
                </div>

                <div>
                  <span>Premiados</span>

                  <strong>
                    {Number(
                      resultadoProcessamento.resumo
                        ?.total_premiados ||
                        resultadoProcessamento.fechamento
                          ?.total_premiados ||
                        0
                    )}
                  </strong>
                </div>

                <div>
                  <span>Apurações</span>

                  <strong>
                    {Number(
                      resultadoProcessamento.resumo
                        ?.apuracoes_simuladas ||
                        resultadoProcessamento
                          .apuracoes_criadas ||
                        0
                    )}
                  </strong>
                </div>

                <div>
                  <span>Conquistas</span>

                  <strong>
                    {Number(
                      resultadoProcessamento.resumo
                        ?.conquistas_simuladas ||
                        resultadoProcessamento
                          .conquistas_criadas ||
                        0
                    )}
                  </strong>
                </div>

                <div>
                  <span>Volume</span>

                  <strong>
                    {dinheiro(
                      resultadoProcessamento.resumo
                        ?.volume_total ||
                        resultadoProcessamento.fechamento
                          ?.volume_total ||
                        0
                    )}
                  </strong>
                </div>
              </div>
            </section>
          )}

          {resultadoReprocessamento && (
  <section className="experienceApuracoes-reprocessPanel">
    <div className="experienceApuracoes-panelHeader">
      <div>
        <span className="experienceApuracoes-reprocessKicker">
          Pós-fechamento
        </span>

        <h2>
          {resultadoReprocessamento.simulacao
            ? "Prévia do reprocessamento"
            : "Competência reprocessada"}
        </h2>

        <p>
          {resultadoReprocessamento.simulacao
            ? "Comparação entre o fechamento original e as operações atualmente aprovadas para a competência."
            : "As alterações identificadas foram aplicadas à competência."}
        </p>
      </div>

      <span
        className={`experienceApuracoes-resultStatus ${
          resultadoReprocessamento.simulacao
            ? "simulacao"
            : "concluido"
        }`}
      >
        {resultadoReprocessamento.simulacao
          ? "Simulação"
          : "Concluído"}
      </span>
    </div>

    <div className="experienceApuracoes-reprocessSummary">
      <div>
        <span>Verificados</span>
        <strong>
          {Number(
            resumoReprocessamento
              .participantes_verificados || 0
          )}
        </strong>
      </div>

      <div>
        <span>Apurações alteradas</span>
        <strong>
          {Number(
            resumoReprocessamento
              .apuracoes_alteradas || 0
          )}
        </strong>
      </div>

      <div
        className={
          Number(
            resumoReprocessamento
              .novas_conquistas || 0
          ) > 0
            ? "destaque"
            : ""
        }
      >
        <span>Novas conquistas</span>
        <strong>
          {Number(
            resumoReprocessamento
              .novas_conquistas || 0
          )}
        </strong>
      </div>

      <div
        className={
          Number(
            resumoReprocessamento
              .revisoes_necessarias || 0
          ) > 0
            ? "atencao"
            : ""
        }
      >
        <span>Requerem revisão</span>
        <strong>
          {Number(
            resumoReprocessamento
              .revisoes_necessarias || 0
          )}
        </strong>
      </div>
    </div>

    <div className="experienceApuracoes-reprocessVolumes">
      <div>
        <span>Volume no fechamento</span>
        <strong>
          {dinheiro(
            resumoReprocessamento
              .volume_anterior
          )}
        </strong>
      </div>

      <RefreshCcw size={17} />

      <div>
        <span>Volume recalculado</span>
        <strong>
          {dinheiro(
            resumoReprocessamento
              .volume_atual
          )}
        </strong>
      </div>

      <div className="experienceApuracoes-reprocessPremiados">
        <span>Premiados</span>
        <strong>
          {Number(
            resumoReprocessamento
              .premiados_anteriores || 0
          )}
          {" → "}
          {Number(
            resumoReprocessamento
              .premiados_atuais || 0
          )}
        </strong>
      </div>
    </div>

    {!temAlteracoesReprocessamento && (
      <div className="experienceApuracoes-reprocessOk">
        <CheckCircle2 size={18} />

        <div>
          <strong>
            Competência atualizada
          </strong>

          <span>
            Não foram encontradas operações posteriores que alterem esta apuração.
          </span>
        </div>
      </div>
    )}

    {alteracoesReprocessamento.length > 0 && (
      <div className="experienceApuracoes-reprocessChanges">
        <div className="experienceApuracoes-reprocessChangesHeader">
          <div>
            <h3>Alterações encontradas</h3>

            <p>
              Somente participantes com diferença em relação ao fechamento são exibidos.
            </p>
          </div>

          <span>
            {alteracoesReprocessamento.length}
          </span>
        </div>

        <div className="experienceApuracoes-reprocessList">
          {alteracoesReprocessamento.map(
            (item) => (
              <article
                key={item.apuracao_id}
                className={`experienceApuracoes-reprocessItem ${
                  item.nova_conquista
                    ? "novaConquista"
                    : ""
                }`}
              >
                <div className="experienceApuracoes-reprocessPerson">
                  <strong>
                    {item.usuario_nome}
                  </strong>

                  <small>
                    ID {item.usuario_id}
                  </small>
                </div>

                <div className="experienceApuracoes-reprocessEvolution">
                  <span>Volume</span>

                  <strong>
                    {dinheiro(
                      item.volume_anterior
                    )}
                    {" → "}
                    {dinheiro(
                      item.volume_atual
                    )}
                  </strong>

                  <small>
                    {Number(
                      item.quantidade_anterior ||
                        0
                    )}
                    {" → "}
                    {Number(
                      item.quantidade_atual ||
                        0
                    )}
                    {" operações"}
                  </small>
                </div>

                <div className="experienceApuracoes-reprocessPin">
                  <span>PIN</span>

                  <strong>
                    {item.pin_anterior_nome ||
                      "Sem PIN"}
                    {" → "}
                    {item.pin_atual_nome ||
                      "Sem PIN"}
                  </strong>

                  {item.nova_conquista && (
                    <small>
                      <Award size={12} />
                      Nova conquista
                    </small>
                  )}
                </div>
              </article>
            )
          )}
        </div>
      </div>
    )}

    {revisoesReprocessamento.length > 0 && (
      <div className="experienceApuracoes-reprocessReview">
        <AlertTriangle size={20} />

        <div>
          <strong>
            {revisoesReprocessamento.length}{" "}
            {revisoesReprocessamento.length === 1
              ? "caso requer"
              : "casos requerem"}{" "}
            revisão administrativa
          </strong>

          <p>
            Uma conquista já concedida atingiu uma faixa superior. O sistema preservou o benefício existente e não realizou a alteração automaticamente.
          </p>
        </div>
      </div>
    )}

    {podeConfirmarReprocessamento && (
      <div className="experienceApuracoes-reprocessConfirm">
        <div>
          <strong>
            Confirmar atualização?
          </strong>

          <p>
            O sistema atualizará as apurações e criará somente as novas conquistas elegíveis.
          </p>
        </div>

        <button
          type="button"
          className="experienceApuracoes-finalizeButton"
          onClick={() =>
            reprocessarCompetencia(false)
          }
          disabled={reprocessando}
        >
          {reprocessando ? (
            <RefreshCcw
              size={17}
              className="experienceApuracoes-spin"
            />
          ) : (
            <CheckCircle2 size={17} />
          )}

          Confirmar reprocessamento
        </button>
      </div>
    )}
  </section>
)}

          <section className="experienceApuracoes-finalize">
  <div>
    <span>
      {fechamentoConcluido
        ? "Competência concluída"
        : "Fechamento da competência"}
    </span>

    <h2>
      {fechamentoConcluido
        ? "Verifique atualizações após o fechamento"
        : "Revise toda a prévia antes de continuar"}
    </h2>

    <p>
      {fechamentoConcluido
        ? "Operações pertencentes a esta competência podem ter sido aprovadas depois do fechamento. Faça uma verificação antes de reprocessar."
        : "A simulação executa todo o processo com rollback. A conclusão definitiva grava as apurações e conquistas."}
    </p>
  </div>

  <div className="experienceApuracoes-finalizeActions">
    {fechamentoConcluido ? (
      <button
        type="button"
        className="experienceApuracoes-reprocessButton"
        onClick={() =>
          reprocessarCompetencia(true)
        }
        disabled={
          reprocessando ||
          processando
        }
      >
        {reprocessando ? (
          <RefreshCcw
            size={17}
            className="experienceApuracoes-spin"
          />
        ) : (
          <RefreshCcw size={17} />
        )}

        Verificar atualizações
      </button>
    ) : (
      <>
        <button
          type="button"
          className="experienceApuracoes-simulateButton"
          onClick={() =>
            processarApuracao(true)
          }
          disabled={processando}
        >
          {processando ? (
            <RefreshCcw
              size={17}
              className="experienceApuracoes-spin"
            />
          ) : (
            <Search size={17} />
          )}

          Simular fechamento
        </button>

        <button
          type="button"
          className="experienceApuracoes-finalizeButton"
          onClick={() =>
            processarApuracao(false)
          }
          disabled={
            processando ||
            !previa.pode_concluir
          }
          title={
            !previa.pode_concluir
              ? "Existem impedimentos para o fechamento."
              : "Concluir apuração definitivamente."
          }
        >
          {processando ? (
            <RefreshCcw
              size={17}
              className="experienceApuracoes-spin"
            />
          ) : (
            <CheckCircle2 size={18} />
          )}

          Concluir apuração
        </button>
      </>
    )}
  </div>
</section>
        </>
      )}
    </div>
  );
}